import type { Person, Pulse, WorldSnapshot } from '~/data/types'

let seq = 0
const nextId = () => `demo-u-${++seq}`

interface Seed {
  name: string
  agent?: boolean
  presence?: Person['presence']
  title?: string
  picture?: string
  gravatar?: string
}

/** A stand-in portrait (no network): a head and shoulders on a coloured tile. */
const portrait = (bg: string, fg: string): string =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><rect width="40" height="40" fill="${bg}"/><circle cx="20" cy="16" r="7" fill="${fg}"/><path d="M7 40 C8 29 32 29 33 40 Z" fill="${fg}"/></svg>`,
  )}`

function makePerson(seed: Seed): Person {
  const userId = nextId()
  return {
    organizationUserId: `ou-${userId}`,
    userId,
    name: seed.name,
    role: 'MEMBER',
    status: 'ACTIVE',
    jobTitle: seed.title ?? null,
    department: null,
    picture: seed.picture ?? null,
    gravatar: seed.gravatar ?? null,
    isAgent: Boolean(seed.agent),
    presence: seed.presence ?? 'online',
    lastSeenAt: null,
  }
}

function makeRoom(id: string, name: string, seeds: Seed[]): { pulse: Pulse; people: Person[] } {
  const people = seeds.map(makePerson)
  return {
    pulse: {
      id,
      name,
      category: 'TEAM',
      type: 'TEAM',
      organizationId: 'demo-org',
      teamThreadId: `${id}-thread`,
      memberUserIds: people.map((p) => p.userId),
      memberCount: people.length,
      unreadCount: 0,
      notificationCount: 0,
      topics: [],
    },
    people,
  }
}

/** A believable fictional org for the no-login demo / engine smoke test. */
export function demoSnapshot(): WorldSnapshot {
  seq = 0
  const rooms = [
    makeRoom('demo-product', 'Product', [
      { name: 'Aki', title: 'PM', presence: 'online' },
      { name: 'Mia', title: 'Engineer', presence: 'online' },
      { name: 'Sora', title: 'Engineer', presence: 'away' },
      { name: 'Nova', agent: true, title: 'Build agent', presence: 'online' },
    ]),
    makeRoom('demo-design', 'Design', [
      { name: 'Sam', title: 'Designer', presence: 'online' },
      { name: 'Lena', title: 'Designer', presence: 'away' },
      { name: 'Pixel', agent: true, title: 'Design agent', presence: 'online' },
    ]),
    makeRoom('demo-growth', 'Growth', [
      { name: 'Ravi', title: 'Marketing', presence: 'online' },
      { name: 'Tom', title: 'Sales', presence: 'offline' },
      { name: 'Yuki', title: 'Analyst', presence: 'online' },
      { name: 'Echo', agent: true, title: 'Growth agent', presence: 'online' },
    ]),
    makeRoom('demo-ops', 'Operations', [
      { name: 'Dan', title: 'Ops lead', presence: 'online' },
      { name: 'Priya', title: 'Finance', presence: 'away' },
      { name: 'Atlas', agent: true, title: 'Ops agent', presence: 'online' },
    ]),
  ]

  // Staff in the company but in none of YOUR pulses — they still show up:
  // humans lounge in the Commons (whatever their presence), unassigned agents
  // idle in the Agent Bay.
  const commons = [
    { name: 'Hana', title: 'People ops', presence: 'online' as const },
    { name: 'Leo', title: 'Legal', presence: 'away' as const },
    { name: 'Ines', title: 'Support', presence: 'offline' as const },
    { name: 'Kai', title: 'IT', presence: 'offline' as const },
  ].map(makePerson)
  const bay = [
    { name: 'Scout', agent: true, title: 'Research agent' },
    { name: 'Quill', agent: true, title: 'Docs agent' },
    { name: 'Relay', agent: true, title: 'Standby agent' },
    // An uploaded picture: the panels keep it (with the ✦ / "AI agent" marks).
    { name: 'Juno', agent: true, title: 'Steward', picture: portrait('#3b3f6b', '#c9c3ff') },
    // A gravatar only, which never counts: the panels show its character.
    { name: 'Bolt', agent: true, title: 'Ops helper', gravatar: portrait('#44403c', '#e7e5e4') },
  ].map(makePerson)

  // A handful of 1:1 conversations so the demo shows the 1:1 wing — most
  // recently active first, one with unread, one long name to prove cropping.
  const now = Date.now()
  const dms: Pulse[] = [
    { name: 'Aki', minsAgo: 4, unread: 2 },
    { name: 'Sam', minsAgo: 40 },
    { name: 'Ravi', minsAgo: 60 * 5 },
    { name: 'Alexandra Montgomery-Whitfield', minsAgo: 60 * 26 },
    { name: 'Dan', minsAgo: 60 * 24 * 3 },
    { name: 'Yuki', minsAgo: 60 * 24 * 9 },
  ].map((dm, i) => ({
    id: `demo-dm-${i}`,
    name: dm.name,
    category: 'ONETOONE',
    type: 'ONETOONE',
    organizationId: 'demo-org',
    teamThreadId: `demo-dm-${i}-thread`,
    memberUserIds: [],
    memberCount: 2,
    unreadCount: dm.unread ?? 0,
    notificationCount: 0,
    topics: [],
    latestUpdate: new Date(now - dm.minsAgo * 60_000).toISOString(),
  }))

  return {
    organizationId: 'demo-org',
    pulses: [...rooms.map((r) => r.pulse), ...dms],
    people: [...rooms.flatMap((r) => r.people), ...commons, ...bay],
    tasksByPulse: {},
    meetings: [],
  }
}
